import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import React from 'react';
import type { ButtonProps as MuiButtonProps } from '@mui/material/Button';
import palette from '@ui/palette';
import Button from '@ui/Button';
import Icon, { AvailableIconTypes } from '@ui/Icon';
import Typography from '@ui/Typography';
import DropdownButton from '../../DropdownButton';
import { alphaWhite } from '../../../util/cssUtils';
import withTooltip from '../../Tooltip/withTooltip';
import { DriveItemType } from '../../Drives/types';

/** Same as legacy `TextButton` for `withTooltip`; routes through `@ui/Button` `variant="text"`. */
const NavTextButton = React.forwardRef<
  HTMLButtonElement,
  Omit<MuiButtonProps, 'variant'>
>(function NavTextButton(props, ref) {
  return <Button ref={ref} variant="text" {...props} />;
});

interface FoldersNavBreadcrumbProps
  extends Pick<NavBreadcrumbProps, 'separatorIcon'> {
  root: string;
  rootHref?: string;
  currentHref?: string;
  children: React.ReactNode;
  onNavigate: (route: string, type?: DriveItemType, name?: string) => void;
  /**
   * When menu button is displayed, the root will also be collapsed into the menu
   */
  collapseRoot?: boolean;
  /**
   * When true, the root is never rendered, neither as a breadcrumb nor as a menu
   * item, and `root`/`rootHref` go unused. Intended for callers that render their
   * own leading crumbs and need every child to count toward the collapse budget.
   * The leading child keeps its separator so the trail reads as a continuation.
   * Defaults to false
   */
  hideRoot?: boolean;
  /**
   * True if the dropdown button component should be rendered in portal
   * Defaults to true
   */
  portal?: boolean;
}

const useItemStyles = makeStyles<{ isMenuItem?: boolean }>()(
  (theme, { isMenuItem }) => ({
    item: {
      maxWidth: isMenuItem ? 'unset' : 200,
      cursor: 'pointer',
      textTransform: 'none',
    },
  })
);

export interface FoldersNavBreadcrumbItemProps {
  id: string;
  itemLabel: string;
  href?: string;
  /**
   * @private for internal use for this component, meant to be set by parent `FoldersNavBreadcrumb` component
   */
  isMenuItem?: boolean;
  onClick?: (id: string, type: DriveItemType) => void;
  type: DriveItemType;
}

export const FoldersNavBreadcrumbItem = ({
  itemLabel,
  isMenuItem,
  ...rest
}: FoldersNavBreadcrumbItemProps) => {
  const { classes } = useItemStyles({ isMenuItem });

  return (
    <div className={classes.item}>
      <Typography variant={isMenuItem ? 'pSm' : 'pMd'} noWrap>
        {itemLabel}
      </Typography>
    </div>
  );
};

const useNavBreadcrumbStyles = makeStyles<{ active: boolean }>()(
  (theme, { active }) => ({
    section: {
      display: 'grid',
      gridAutoFlow: 'column',
      alignItems: 'center',
      gap: theme.spacing(1.5),
      color: palette.gray_75,
    },
    label: {
      color: `${active ? palette.gray_0 : palette.gray_75} !important`,
      '&:hover': {
        backgroundColor: alphaWhite(0.09),
        color: `${palette.gray_0} !important`,
      },
      textTransform: 'none',
    },
  })
);

interface NavBreadcrumbProps {
  children: React.ReactNode;
  active: boolean;
  onClick?: () => void;
  withSeparator?: boolean;
  label?: string;
  separatorIcon?: AvailableIconTypes | 'slash';
}

/**
 * Component wraps its children with a tooltip if label is provided
 * Renders children within a text-style button if label
 */
const NavBreadcrumb = ({
  active,
  children,
  onClick,
  withSeparator = true,
  label,
  separatorIcon = 'chevronRight',
}: NavBreadcrumbProps) => {
  const { classes } = useNavBreadcrumbStyles({ active });

  const separator =
    (withSeparator && separatorIcon === 'slash' && (
      <Typography variant="pMd">/</Typography>
    )) ||
    (withSeparator && (
      <Icon type={separatorIcon as AvailableIconTypes} color="inherit" />
    ));

  return (
    <Box className={classes.section}>
      {separator}
      {label &&
        label !== '' &&
        withTooltip(NavTextButton)({
          disabled: active,
          onClick,
          className: classes.label,
          title: label || '',
          children,
        })}
      {!label && children}
    </Box>
  );
};

const useStyles = makeStyles()(theme => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  menuContent: {
    width: 261,
  },
}));

export const ALL_DRIVES_KEY = 'All workspaces';

/**
 * Display at most 2 items in breadcrumb component
 * Hides all others in menu dropdown
 *
 * @example
 * <FoldersNavBreadcrumb.Container>
 *  // This will be hidden in menu
 *  <FoldersNavBreadcrumb.Item>Folder A</FoldersNavBreadcrumb.Item>
 *
 *  // These will be rendered in UI
 *  <FoldersNavBreadcrumb.Item href="/folderB" label="Folder B">Folder B</FoldersNavBreadcrumb.Item>
 *  <FoldersNavBreadcrumb.Item href="/folderC" label="Folder C">Folder C</FoldersNavBreadcrumb.Item>
 * </FoldersNavBreadcrumb>
 */
export const FoldersNavBreadcrumb = (props: FoldersNavBreadcrumbProps) => {
  const {
    root,
    rootHref,
    currentHref,
    children,
    onNavigate,
    separatorIcon,
    collapseRoot = false,
    hideRoot = false,
    portal,
  } = props;

  const rootMenuItem = (
    <FoldersNavBreadcrumbItem
      id={ALL_DRIVES_KEY}
      itemLabel={ALL_DRIVES_KEY}
      href={ALL_DRIVES_KEY}
      isMenuItem
      onClick={() => onNavigate(ALL_DRIVES_KEY)}
      type={DriveItemType.Drive}
    />
  );

  const menuItems: React.ReactElement[] = [];
  const childArray: React.ReactElement<typeof NavBreadcrumb>[] = [];

  const total = React.Children.count(children);

  const isMenuItem = (index: number) =>
    !(total <= 2) && index < Math.max(total - 2, 0);

  /**
   * Determine which children can be shown as a breadcrumb and which should be
   * collapsed into dropdown menu
   * Assumes that each child component is of type `FoldersNavBreadcrumbItem`
   */
  React.Children.forEach(children, (child, index) => {
    if (React.isValidElement(child)) {
      const shouldBeInMenu = isMenuItem(index);
      if (shouldBeInMenu) {
        const extraProp = {
          onClick: () =>
            onNavigate(
              child.props.href,
              child.props.type,
              child.props.itemLabel
            ),
          isMenuItem: true,
        };
        const updatedChild = React.cloneElement(child, {
          ...extraProp,
        });
        menuItems.push(updatedChild);
      } else if (child.props.itemLabel) {
        childArray.push(
          <NavBreadcrumb
            key={child.props.id}
            label={child.props.itemLabel}
            active={child.props.href?.includes(currentHref)}
            onClick={() =>
              onNavigate(
                child.props.href,
                child.props.type,
                child.props.itemLabel
              )
            }
            separatorIcon={separatorIcon}
          >
            {child}
          </NavBreadcrumb>
        );
      }
    }
  });

  const rootInMenu = collapseRoot && !hideRoot && menuItems.length > 0;

  const menuItemsToDisplay = rootInMenu
    ? [rootMenuItem].concat(menuItems)
    : menuItems;

  const { classes } = useStyles();
  const handleClick = () => {
    if (rootHref) onNavigate(rootHref, DriveItemType.Drive, root);
  };

  const capitalize = (str: string) => {
    if (!str || str === '') return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  const showRoot = !hideRoot && !rootInMenu;
  return (
    <Box className={classes.container}>
      {showRoot && (
        <NavBreadcrumb
          label={capitalize(root)}
          active={childArray.length === 0}
          withSeparator={false}
          onClick={handleClick}
        >
          <Typography variant="pMd">{capitalize(root)}</Typography>
        </NavBreadcrumb>
      )}
      {menuItems.length > 0 && (
        <NavBreadcrumb
          active={false}
          withSeparator={!rootInMenu}
          separatorIcon={separatorIcon}
        >
          <DropdownButton
            id="folder-breadcrumb"
            iconType="moreHoriz"
            variant="icon"
            classes={{
              menuContent: classes.menuContent,
            }}
            portal={portal}
          >
            {menuItemsToDisplay}
          </DropdownButton>
        </NavBreadcrumb>
      )}
      {childArray}
    </Box>
  );
};

export default FoldersNavBreadcrumb;
